// cSpell:ignore Afrekening Eindafrekening Naam vertrek
// How the dashboard names a settlement (Lewis, 2026-10-16): by the name the
// broker gave it in the period step ("Naam", prefilled with its kind and
// period: "Afrekening 2026", "Eindafrekening bij vertrek";
// getSettlementTitle). A settlement without a stored name, a draft whose
// name still follows the suggestion or an older one, is named by
// getSettlementTitle. A departure adds its tenants in the broker's lists,
// never towards the tenant (Q2). There is no description of its own.

import {
  getSettlementTitle,
  TSettlementKind,
} from "@rentiohq/shared-frontend/dist/utils/settlement.utils";
import type { Settlement } from "./types";

const TITLE_SEPARATOR = " — ";

type TNamedSettlement = Pick<
  Settlement,
  "periodFrom" | "periodTo" | "snapshot" | "draftState"
>;

// A departure settlement names its lease in the snapshot once confirmed, in
// the draft before.
export const settlementKindOf = (
  settlement: Pick<Settlement, "snapshot" | "draftState">,
): TSettlementKind =>
  settlement.snapshot?.departure || settlement.draftState?.departure
    ? "departure"
    : "regular";

// The name the broker gave the settlement: frozen in the snapshot once
// confirmed, in the draft before; undefined when it has none.
export const settlementStoredName = (
  settlement: Pick<Settlement, "snapshot" | "draftState">,
): string | undefined =>
  settlement.snapshot?.name?.trim() ||
  settlement.draftState?.name?.trim() ||
  undefined;

// A settlement's name: the stored one, else its kind and period
// (getSettlementTitle) in `locale` or the reader's language.
export const settlementTitleOf = (
  settlement: TNamedSettlement,
  options: { locale?: string; withLeaseEnd?: boolean } = {},
): string =>
  settlementStoredName(settlement) ??
  getSettlementTitle({
    kind: settlementKindOf(settlement),
    from: settlement.periodFrom,
    to: settlement.periodTo,
    ...options,
  });

// A name followed by a departure's tenants, unless the name already says
// them.
const withTenantName = (name: string, tenantName?: string): string => {
  const tenant = tenantName?.trim();
  if (!tenant || name.toLowerCase().includes(tenant.toLowerCase())) {
    return name;
  }
  return `${name}${TITLE_SEPARATOR}${tenant}`;
};

// A settlement as the dashboard's lists, its detail and its messages name
// it, in the broker's language: its name, and a departure's tenants.
export const settlementDisplayName = (settlement: TNamedSettlement): string =>
  withTenantName(
    settlementTitleOf(settlement),
    settlementKindOf(settlement) === "departure"
      ? settlement.snapshot?.departure?.tenantName
      : undefined,
  );

// A settlement the API names in a refusal (its ISettlementRef), in the
// reader's language: its name, else its kind and period, and a departure's
// tenant. A refusal that does not say the kind names a regular settlement.
export const settlementRefName = (ref: {
  name?: string;
  periodFrom: string;
  periodTo: string;
  kind?: TSettlementKind;
  tenantName?: string;
}): string => {
  const kind = ref.kind ?? "regular";
  const name =
    ref.name?.trim() ||
    getSettlementTitle({ kind, from: ref.periodFrom, to: ref.periodTo });
  return withTenantName(
    name,
    kind === "departure" ? ref.tenantName : undefined,
  );
};

// The settlement a refusal of the API names, as it sends it next to its
// message (rentio-api-next toSettlementRefBody, the departure-exists
// refusal): undefined when the refusal does not carry one with a period.
export const refusalSettlementRef = (
  value: unknown,
): Parameters<typeof settlementRefName>[0] | undefined => {
  if (value == null || typeof value !== "object") return undefined;
  const { name, periodFrom, periodTo, kind, tenantName } = value as Record<
    string,
    unknown
  >;
  if (typeof periodFrom !== "string" || typeof periodTo !== "string") {
    return undefined;
  }
  return {
    periodFrom,
    periodTo,
    ...(typeof name === "string" ? { name } : {}),
    ...(kind === "departure" || kind === "regular" ? { kind } : {}),
    ...(typeof tenantName === "string" ? { tenantName } : {}),
  };
};
